import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { changeLanguage } from '../utils/i18n';

// 定义语言类型
export type LanguageType = 'en' | 'zh' | 'ru' | 'ur' | 'ko';

// 定义语言上下文接口
interface LanguageContextType {
  language: LanguageType;
  setLanguage: (language: LanguageType) => void;
  toggleLanguage: () => void;
}

// 创建语言上下文
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 定义Provider属性接口
interface LanguageProviderProps {
  children: ReactNode;
}

// 语言Provider组件
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // 从本地存储获取初始语言，默认为中文
  const [language, setLanguageState] = useState<LanguageType>(
    (localStorage.getItem('language') as LanguageType) || 'zh'
  );

  // 设置语言函数
  const setLanguage = (newLanguage: LanguageType) => {
    setLanguageState(newLanguage);
    changeLanguage(newLanguage);
  };

  // 切换语言函数 - 循环切换所有支持的语言
  const toggleLanguage = () => {
    const languageOrder: LanguageType[] = ['zh', 'en', 'ru', 'ur', 'ko'];
    const currentIndex = languageOrder.indexOf(language);
    const nextIndex = (currentIndex + 1) % languageOrder.length;
    const newLanguage = languageOrder[nextIndex];
    setLanguage(newLanguage);
  };

  // 初始化时设置语言
  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  // 提供上下文值
  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    toggleLanguage
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// 使用语言上下文的钩子
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext; 